import { useCallback, useEffect, useState } from "react";
import { Eye, EyeOff, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "./AuthContext";

const LABEL = { company: "Company", service: "Service", industry: "Industry", procedure: "Procedure", product_status: "Product status", support: "Support", contact: "Contact" };
const blank = { category: "company", title: "", body: "", link: "", published: false };
const inputCls = "mt-1 w-full rounded-xl border border-white/10 bg-[#0C0A09] px-3 py-2 text-sm text-stone-100 outline-none focus:border-orange-500";

const Editor = ({ initial, categories, onSave, onCancel, busy }) => {
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  return (
    <div className="rounded-2xl border border-orange-500/30 bg-[#141110] p-5" data-testid="kb-editor">
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="text-xs text-stone-400">Category
          <select value={f.category} onChange={set("category")} className={inputCls} data-testid="kb-editor-category">
            {categories.map((c) => <option key={c} value={c}>{LABEL[c] ?? c}</option>)}
          </select>
        </label>
        <label className="text-xs text-stone-400 sm:col-span-2">Title
          <input value={f.title} onChange={set("title")} maxLength={120} className={inputCls} data-testid="kb-editor-title" />
        </label>
      </div>
      <label className="mt-3 block text-xs text-stone-400">Approved content ({f.body.length}/2000)
        <textarea value={f.body} onChange={set("body")} rows={4} maxLength={2000} className={inputCls} data-testid="kb-editor-body" />
      </label>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="text-xs text-stone-400">Related page (internal path, optional)
          <input value={f.link ?? ""} onChange={set("link")} placeholder="/services/…" maxLength={120} className={inputCls} data-testid="kb-editor-link" />
        </label>
        <label className="mt-5 flex items-center gap-2 text-sm text-stone-200">
          <input type="checkbox" checked={f.published} onChange={set("published")} className="h-4 w-4 accent-orange-500" data-testid="kb-editor-published" />
          Published (available to the AI and public concierge)
        </label>
      </div>
      <div className="mt-4 flex gap-2">
        <button type="button" disabled={busy} onClick={() => onSave(f)} className="tc-btn-primary inline-flex h-10 items-center gap-2 rounded-full px-5 text-sm font-semibold" data-testid="kb-editor-save">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save entry
        </button>
        <button type="button" onClick={onCancel} className="h-10 rounded-full border border-white/15 px-4 text-sm text-stone-300" data-testid="kb-editor-cancel">Cancel</button>
      </div>
    </div>
  );
};

const Pending = ({ e, approver, onReview }) => (
  <div className="mt-2 rounded-xl border border-amber-400/30 bg-amber-400/10 p-2 text-xs text-amber-100" data-testid={`kb-pending-${e.key}`}>
    Change proposed by {e.pending.by}, awaiting Super Administrator approval: {Object.entries(e.pending.changes ?? {}).map(([k, v]) => `${k}: ${String(v).slice(0, 80)}`).join(" · ")}
    {approver && <span className="ml-2 inline-flex gap-1">
      <button type="button" onClick={() => onReview("approve")} className="rounded-full bg-emerald-400/20 px-2 py-0.5 text-emerald-100" data-testid={`kb-approve-${e.key}`}>Approve</button>
      <button type="button" onClick={() => onReview("reject")} className="rounded-full bg-red-500/20 px-2 py-0.5 text-red-100" data-testid={`kb-reject-${e.key}`}>Reject</button>
    </span>}
  </div>
);

const Row = ({ e, edit, approver, onEdit, onToggle, onDelete, onReview }) => (
  <li className="border-b border-white/10 px-5 py-4" data-testid={`kb-entry-${e.key}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <p className="text-sm text-stone-100"><span className="mr-2 rounded-full border border-white/15 px-2 py-0.5 font-mono text-[10px] uppercase text-stone-400">{LABEL[e.category]}</span>{e.title}</p>
        <p className="mt-1.5 text-xs leading-relaxed text-stone-400">{e.body}</p>
        <p className="mt-1 text-[11px] text-stone-500">{e.link ? `${e.link} · ` : ""}Updated {e.updated_at?.slice(0, 10)} by {e.updated_by}{e.sensitive ? " · sensitive: changes need Super Administrator approval" : ""}</p>
        {e.pending && <Pending e={e} approver={approver} onReview={onReview} />}
      </div>
      {edit && <div className="flex shrink-0 items-center gap-1.5">
        <button onClick={onToggle} data-testid={`kb-toggle-${e.key}`} aria-label={e.published ? "Unpublish" : "Publish"}
          className={`inline-flex h-8 items-center gap-1 rounded-full px-3 text-xs ${e.published ? "bg-emerald-400/15 text-emerald-200" : "bg-white/5 text-stone-400"}`}>
          {e.published ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}{e.published ? "Published" : "Draft"}
        </button>
        <button onClick={onEdit} aria-label="Edit entry" data-testid={`kb-edit-${e.key}`} className="grid h-8 w-8 place-items-center rounded-full border border-white/15 text-stone-300 hover:border-orange-500"><Pencil className="h-3.5 w-3.5" /></button>
        <button onClick={onDelete} aria-label="Delete entry" data-testid={`kb-delete-${e.key}`} className="grid h-8 w-8 place-items-center rounded-full border border-white/15 text-stone-300 hover:border-red-400 hover:text-red-300"><Trash2 className="h-3.5 w-3.5" /></button>
      </div>}
    </div>
  </li>
);

export const KnowledgeBase = () => {
  const { can } = useAuth();
  const [data, setData] = useState({ categories: [], entries: [] });
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    try {
      const k = await api.get("/admin/knowledge");
      setData({ categories: k.data?.categories ?? [], entries: Array.isArray(k.data?.entries) ? k.data.entries : [] });
    } catch (err) { toast.error(formatApiError(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (f) => {
    setBusy(true);
    try {
      const body = { category: f.category, title: f.title, body: f.body, link: f.link || (f.id ? "" : null), published: f.published };
      const { data: saved } = f.id ? await api.patch(`/admin/knowledge/${f.id}`, body) : await api.post("/admin/knowledge", body);
      toast.success(saved?.pending ? "Change submitted for Super Administrator approval" : "Knowledge entry saved");
      setEditing(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const toggle = async (e) => {
    try { await api.patch(`/admin/knowledge/${e.id}`, { published: !e.published }); load(); } catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (e) => {
    if (!window.confirm(`Delete "${e.title}"? The AI will no longer use it.`)) return;
    try { await api.delete(`/admin/knowledge/${e.id}`); toast.success("Entry deleted"); load(); } catch (err) { toast.error(formatApiError(err)); }
  };
  const shown = data.entries.filter((e) => !filter || e.category === filter);

  return (
    <div className="mt-3" data-testid="admin-knowledge">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <select value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Filter by category" data-testid="kb-filter"
          className="h-10 rounded-full border border-white/10 bg-[#141110] px-4 text-sm text-stone-200 outline-none">
          <option value="">All categories</option>
          {data.categories.map((c) => <option key={c} value={c}>{LABEL[c] ?? c}</option>)}
        </select>
        {can("kb.edit") && <button onClick={() => setEditing({ ...blank })} data-testid="kb-new-btn" className="ml-auto inline-flex h-10 items-center gap-2 rounded-full bg-orange-500 px-4 text-sm font-semibold text-black">
          <Plus className="h-4 w-4" /> New entry
        </button>}
      </div>
      {editing && <div className="mb-4"><Editor initial={editing} categories={data.categories} busy={busy} onSave={save} onCancel={() => setEditing(null)} /></div>}
      <ul className="overflow-hidden rounded-2xl border border-white/10" data-testid="kb-list">
        {shown.length === 0 && <li className="p-8 text-center text-sm text-stone-400">No entries.</li>}
        {shown.map((e) => <Row key={e.id} e={e} edit={can("kb.edit")} approver={can("kb.approve")} onEdit={() => setEditing({ ...e, link: e.link ?? "" })} onToggle={() => toggle(e)} onDelete={() => remove(e)}
          onReview={async (dec) => { try { await api.post(`/admin/knowledge/${e.id}/${dec}`); toast.success(dec === "approve" ? "Change approved" : "Change rejected"); load(); } catch (err) { toast.error(formatApiError(err)); } }} />)}
      </ul>
    </div>
  );
};
